import { getGoogleOAuthClientId } from './googleOAuthClientId';
import { extractGoogleErrorCode, messageForGoogleErrorCode } from './googleErrorMessages';

/** GCP project number from API-disabled error text, if present. */
export function parseDisabledClassroomApiProject(err: unknown): string | null {
  const msg = err instanceof Error ? err.message : String(err);
  if (
    !/classroom api has not been used|api has not been enabled|access not configured/i.test(msg)
  ) {
    return null;
  }
  return msg.match(/project\s+(\d+)/i)?.[1] ?? null;
}

export function isClassroomApiConfigError(err: unknown): boolean {
  return parseDisabledClassroomApiProject(err) != null;
}

export function expectedOAuthProjectNumber(): string | null {
  const id = getGoogleOAuthClientId();
  return id ? (id.split('-')[0] ?? null) : null;
}

/** Stale token from a different OAuth client than VITE_GOOGLE_CLIENT_ID. */
export function isStaleOAuthProjectError(err: unknown): boolean {
  const errProject = parseDisabledClassroomApiProject(err);
  const clientProject = expectedOAuthProjectNumber();
  return !!(errProject && clientProject && errProject !== clientProject);
}

/** User-facing message for Classroom sync failures (no tokens). */
export function classroomSyncErrorMessage(err: unknown): string {
  // Prefer the stable structured code when the server provided one — never
  // guess from message text if we have an authoritative code.
  const structured = messageForGoogleErrorCode(extractGoogleErrorCode(err));
  if (structured) return structured;

  const msg = err instanceof Error ? err.message : String(err);
  if (/classroom api has not been used|api has not been enabled|access not configured/i.test(msg)) {
    const projectMatch = msg.match(/project\s+(\d+)/i);
    const project = projectMatch?.[1];
    return project
      ? `Google Classroom API is disabled for GCP project ${project}. In Google Cloud Console → APIs & Services → Library, enable “Google Classroom API” for that project (the same project as your OAuth Web client).`
      : 'Google Classroom API is disabled for your OAuth project. Enable “Google Classroom API” in Google Cloud Console → APIs & Services → Library.';
  }
  if (/403|permission|insufficient/i.test(msg)) {
    return 'Google Classroom access was denied. Try Connect again and approve all Classroom permissions.';
  }
  if (/401|invalid credentials|invalid_grant/i.test(msg)) {
    return 'Google sign-in expired. Use Connect Google Classroom again.';
  }
  if (/network|failed to fetch|timed out/i.test(msg)) {
    return 'Could not reach Google Classroom. Check your internet connection and try Retry.';
  }
  if (isStaleOAuthProjectError(err)) {
    const errProject = parseDisabledClassroomApiProject(err);
    const clientProject = expectedOAuthProjectNumber();
    return `Your saved Google sign-in uses GCP project ${errProject}, but this app is configured for project ${clientProject}. Tap Connect Google Classroom again.`;
  }
  return 'Could not sync courses from Google Classroom. Use Retry or check Google Cloud Console settings.';
}
